(ns app.front.view.workspace.util
  (:require [app.domain.application.model :as app-model]
            [app.domain.branding.model :as branding-model]
            [app.domain.property.model :as property-model]
            [app.domain.workspace.closure-category-model :as closure-category-model]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [potpuri.core :as p]))

(defn workspace-view-data
  "Entity-derived base for the :view/workspace app-db map. Shared by the canonical
  workspace fetch (app.front.view.application/workspace-fetch-success) and the
  application wizard (app.front.view.application-wizard.core/set-workspace-data-for-step)
  so both writers keep the same key shape."
  [workspace application execution authority-group]
  (p/assoc-if {:workspace/id   (:workspace/id workspace)
               :application-id (:db/id application)
               :group/id       (:group/id authority-group)
               :permit-type    (:workspace/permit-type workspace)}
              :execution-id (:db/id execution)))

(defn workspace-href [workspace]
  (href/href :view/workspace
             {:workspace-id (:workspace/id workspace)}))

(defn search-origin-query-params [match]
  (when (= "search" (get-in match [:query-params :origin]))
    {:origin "search"}))

(defn resume-application? [workspace]
  (and (ff/enabled? :feature-flag.id/application-wizard)
       (or (#{:state.application/draft :state.application/pre-submit-payment}
            (:phase-state workspace))
           (#{app-model/initial-domain-state :state.application/pre-submit-payment}
            (-> workspace workspace-model/workspace-application :domain/state))
           (:resume-application? workspace))
       (not (:has-pre-consultation? workspace))
       (not (workspace-model/own-or-prior-pre-consultation workspace))))

(defn workspace-or-resume-application-href
  ([workspace]
   (workspace-or-resume-application-href workspace (workspace-href workspace)))
  ([workspace workspace-href]
   (workspace-or-resume-application-href workspace workspace-href nil))
  ([{:workspace/keys [id]
     :group/keys     [uri-path-id]
     :as             workspace}
    workspace-href query-params]
   (if (resume-application? workspace)
     (href/href :view/application-wizard-resume {:workspace-id id} query-params)
     (if uri-path-id
       (href/href :view/workspace
                  {:branding     (branding-model/or-default uri-path-id)
                   :workspace-id id}
                  query-params)
       (if query-params
         (href/href :view/workspace {:workspace-id id} query-params)
         workspace-href)))))

(defn workspace-link-description [workspace]
  (if (resume-application? workspace)
    :search/resume-application
    :search/open-application))

(defn pre-consultation-href [workspace]
  (href/href :view/workspace-pre-consultation
             {:workspace-id (:workspace/id workspace)}))

(defn workspace-state
  "Print the workspace state along with the state of the current phase. The tr function does not need to be wrapped
  with the permit-type specific replacements, that will be done here. But it can be."
  [tr {:keys [workspace-state phase-state :workspace/permit-type]}]
  (let [tr (i18n-model/wrap-tr-fn (get i18n-model/permit-type-specific-keys permit-type)
                                  tr)]
    (str/join " "
              [(tr [phase-state])
               (when (and (not= workspace-state :workspace.state/application)
                          ;; for code-enforcement, execution is the default state
                          (not= permit-type :permit-type/CE))
                 (str "(" (tr [workspace-state]) ")"))])))

(defn status-text [status workspace]
  (closure-category-model/status-text status
                                      (ff/enabled? :feature-flag.id/closure-category-visibility)
                                      (<sub [:language]) workspace))

(defn property-status [row]
  (status-text (:state row) row))

(defn permit-type-label
  "Permit type name of an Elasticsearch workspace result, shown to users.

  The group-specific permit type name is used when available, otherwise the permit type keyword
  is translated."
  [tr lang {:workspace/keys [permit-type permit-type-names]}]
  (or (i18n-model/get-text-from-entities-or-map permit-type-names lang)
      (tr [permit-type])))

(defn search-result-address
  "Address of an Elasticsearch workspace result, with its unit info.

  Falls back to the address-missing text and the first property id when the workspace has no
  address of its own."
  [tr lang {:workspace/keys [address address-unit-part] :as es-workspace}]
  (or (property-model/format-address-with-units address (i18n-model/get-text address-unit-part lang))
      (str (tr [:address/address-missing])
           " " (-> es-workspace :property/id first))))

(defn workspace-emphasis [phase-state]
  (when (contains? #{:state.application/refused
                     :state.application/denied}
                   phase-state)
    "text-ui-red-100"))
